import React, { KeyboardEvent, useEffect, useState } from 'react';
import { Box, Button, Checkbox, FormControl, FormControlLabel, Grid, Hidden, IconButton, InputLabel, MenuItem, Select, Switch, TextField, Tooltip, Typography } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';

import { MdClose, MdHelpOutline, MdAddCircleOutline, MdRemoveCircleOutline } from 'react-icons/md';

import { useSelector, useDispatch } from 'reducer';
import { updateEditions, updateRoyalties, updateTags, updateField, updateRoyaltiesSharing, updateGenerator } from 'reducer/slices/createNft';
import formatNumber from 'utils/formatNumber';

import { findCollectionsByOwner } from 'services/collections';

import { categories } from '../../data';
import * as S from './styles';

export type RoyaltSharingProps = {
	walletAddress?: string; 
	royalties?: string;
};

export default function Form({ handleNextStep }: ViewComponentProps) {
	const dispatch = useDispatch();

	const { createNft: state, system } = useSelector(s => s);
	const { 
		fields: { name, description, collection_name, category: categoryName }, 
		editions, 
		royalties, 
		tags: tagsList, 
		royaltiesSharing, 
		selectedFile,
		displayImageFile,
		isGenerator,
	} = state;

	const [checkedRoyalty, setCheckedRoyalty] = useState<boolean>(!!royaltiesSharing?.length);
	const [editionsToken, setEditionsToken] = useState<string>(editions?.toString() || '1');
	const [formDescription, setFormDescription] = useState<string>(description || '');
	const [collectionName, setCollectionName] = useState<string>(collection_name || '');
	const [category, setCategory] = useState<string>(categoryName || '');
	const [formName, setFormName] = useState<string>(name || '');
	const [isGeneratorToken, setIsGeneratorToken] = useState<boolean>(isGenerator || false);
	const [royaltiesList, setRoyaltiesList] = useState<RoyaltSharingProps[]>(royaltiesSharing ? 
		royaltiesSharing.map(({ walletAddress, royalties }) => ({ walletAddress, royalties: royalties.toFixed(2).toString() })) 
		: []
	);
	const [royaltiesToken, setRoyaltiesToken] = useState<string>(royalties?.toString() || '10.00');
	const [tag, setTag] = useState<string>('');
	const [tags, setTags] = useState<string[]>(tagsList || []);

	const [errorMessage, setErrorMessage] = useState({
		editions: '',
		royalties: '',
	});

	const [royaltiesSharingError, setRoyaltiesSharingError] = useState<{[x: number]: string}>({});
	const [collections, setCollections] = useState<string[]>([]);

	useEffect(() => {
		if (!editions) dispatch(updateEditions(1));
		if (!royalties) dispatch(updateRoyalties(10));

		dispatch(updateGenerator(false));

		(async () => {
			const response = await findCollectionsByOwner(system.tzPublicKey!);

			const collectionsNames = response.map(({ name }) => name);
			setCollections(collectionsNames);
		})();

		const delayDebounceFunction = setTimeout(() => {
			handleRoyaltiesSharing();
		}, 3000);

		return () => clearTimeout(delayDebounceFunction);
	}, []);

	function handleCreateTag(event: KeyboardEvent) {
		if (tags.length >= 5) {
			return false;
		}

		const splittedTag = tag.split(/[,\s]+/).filter(item => !!item.trim());
		const replacedTrimmedTag = tag.trim().replaceAll(',', '').toLowerCase();

		if (splittedTag.length > 1) {
			const newTags = [...tags, ...splittedTag];

			setTags(newTags);
			setTag('');
			dispatch(updateTags(newTags));				
		} else if ([13, 32, 188].includes(event.keyCode) && replacedTrimmedTag.length) {
			const newTags = [...tags, replacedTrimmedTag];

			setTags(newTags);
			setTag('');

			if (newTags.length === 5) {
				dispatch(updateTags(newTags));				
			}
		}
	}
	
	function handleDeleteTag(index: number) {		
		let newTags = [...tags];
		newTags.splice(index, 1);
		
		setTags([...newTags]);
		dispatch(updateTags(newTags));
	}

	function handleEditions(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
		const replaced = replacedParsedValue(e.target.value);
		const parsed = Number(replaced);

		if (isNaN(parsed)) {
			return false;
		}

		setEditionsToken(parsed.toString());

		if (parsed <= 0) {
			setErrorMessage({ ...errorMessage, editions: "Editions needs to be greater than 0 and between 1 and 10" });
			return false;		
		}

		if (parsed > 10) {
			setErrorMessage({ ...errorMessage, editions: "Editions needs to be lower than 10" });
			return false;		
		}
		
		setErrorMessage({ ...errorMessage, editions: "" });
	}

	function replacedParsedValue(value: string) {
		return value
			.toString()
			.replace(/[^0-9\.]+/g, '')
			.replace(/[R$.]*/g, '')
			.replace(',', '.')
			.trim();
	}
		
	function formatValueWithDecimals(value: number) {
		return Number((Math.round((value / 100) * 100) / 100).toFixed(2));
	}

	function formatValue(value: number) {
		return new Intl.NumberFormat('en-EN', {
			minimumFractionDigits: 2,
		}).format(value);
	}

	function handleFormattedRoyalties(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
		errorMessage.royalties = '';
		const replaced = replacedParsedValue(e.target.value);
		const formattedValue = formatValueWithDecimals(Number(replacedParsedValue(replaced)));

		if (isNaN(formattedValue)) {
			return false;
		}

		if (formattedValue > 30) {
			setErrorMessage({ ...errorMessage, royalties: "Royalties are limited up to 30%" });
			return false;		
		}
				
		setRoyaltiesToken(formatValue(formattedValue));
	}

	function handleCategoryChange(event: any) {
		setCategory(event.target.value as string)
	}
	
	function handleAddRoyalties() {
		setRoyaltiesList([...royaltiesList, {}]);
	}

	function handleDeleteRoyalties(index: number) {
		if (royaltiesSharingError[index]) {
			setRoyaltiesSharingError({ ...royaltiesSharingError, [index]: '' });
		}

		const newRoyaltiesList = royaltiesList;
		royaltiesList.splice(index, 1);

		setRoyaltiesList([...newRoyaltiesList]);
		handleRoyaltiesSharing();
	}

	function handleRoyaltiesList(index: number, prop: 'walletAddress' | 'royalties', value: string) {		
		const newRoyaltiesList = royaltiesList;

		// if (royaltiesList[index]?.walletAddress){
		// 	// Create an array with "group by" style by walletAddresses
		// 	const walletAdressReduce: { [x: string]: [] } = royaltiesList.reduce(function (r, a) {
		// 		r[a.walletAddress!] = r[a.walletAddress!] || [];
		// 		r[a.walletAddress!].push(a);
		// 		return r;
		// 	}, Object.create(null));
	
		// 	// This code is to "break" the forEach condition if find any error
		// 	[0].every(() => {
		// 		Object.entries(walletAdressReduce).forEach(([key, values]) => {
		// 			if (values.length > 1) {
		// 				setRoyaltiesSharingError({ 
		// 					...royaltiesSharingError, 
		// 					[index]: (key === system.tzPublicKey!) ? "You can not add your own wallet" : "Wallet address already used"
		// 				});

		// 				return false;
		// 			}
		// 		});
	
		// 		return true;
		// 	});
		// }
		
		if (prop === 'walletAddress') {						
			delete royaltiesSharingError[index];

			const filteredRoyaltiesListSharing = royaltiesList.filter(({ walletAddress }) => walletAddress === value);
			setRoyaltiesSharingError({ ...royaltiesSharingError });

			if (value === system.tzPublicKey!) {
				setRoyaltiesSharingError({ ...royaltiesSharingError, [index]: "You can not add your own wallet" });
			} else if (filteredRoyaltiesListSharing.length) {
				setRoyaltiesSharingError({ ...royaltiesSharingError, [index]: 'Wallet address already used' });
			}
			
			newRoyaltiesList[index][prop] = value;
		} else {
			const formattedRateValue = formatNumber(value);
			newRoyaltiesList[index][prop] = !isNaN(Number(formattedRateValue)) ? formattedRateValue.toString() : '';
		}
		
		setRoyaltiesList([...newRoyaltiesList]);
	}

	function handleRoyaltiesSharing() {
		if (royaltiesList.length >= 1) {
			const newRoyaltiesList = royaltiesList;
			const value = 100 - royaltiesList.slice(1).reduce((acc, { royalties }, index) => {
				const accumulator = acc + Number(royalties || 0); 
	
				if (accumulator >= 100) {
					newRoyaltiesList[index + 1].royalties = (100 - acc).toFixed(2).toString();
	
					return 100;
				}
	
				return accumulator;
			}, 0);
	
			newRoyaltiesList[0].royalties = value.toFixed(2).toString();
			setRoyaltiesList([...newRoyaltiesList]);
		}
	}

	function handleCheckedRoyaltySharing(event: React.ChangeEvent<HTMLInputElement>) {
		const isChecked = event.target.checked
		setCheckedRoyalty(isChecked);

		if (isChecked) {
			if (!royaltiesList.length) {
				const startedNewArray = [
					{ walletAddress: system.tzPublicKey!, royalties: '100'  },
					{ walletAddress: '', royalties: ''  }
				];
				setRoyaltiesList([...royaltiesList, ...startedNewArray]);
	
				return;
			}
		}

		setRoyaltiesList([]);
	}

	function handleCreateToken() {
		if (![name, editionsToken, royaltiesToken].every(item => !!item)){
			alert("Fields marked with (*) are mandatory");
			return false;
		}

		if (Object.keys(royaltiesSharingError).length) {
			alert("You have errors on your royalty sharing map");
			return false;
		}

		if (!Object.values(errorMessage).every(item => !item)) {
			alert("You have errors on your form");
			return false;
		}

		if (!selectedFile) {
			alert("You need to upload a file");
			return false;
		} else {
			const type = selectedFile.name.split('.')[1].trim();

			if (['zip', 'glb'].includes(type) && !displayImageFile) {
				alert("You need to upload a display image for this format");
				return false;
			}
		}

		if (royaltiesList.length > 0) {
			const hasEmptyFields = royaltiesList.some(({ walletAddress, royalties }) => !walletAddress || !royalties);
			const parsedRoyaltiesList = royaltiesList.map(({ walletAddress, royalties }) => { 
				return { 
					walletAddress: walletAddress!, 
					royalties: Number(royalties)
				}
			});
			
			if (hasEmptyFields) {
				alert("You need to fill up all royalties fields");
				return 	
			}

			dispatch(updateRoyaltiesSharing(parsedRoyaltiesList));
		}

		handleNextStep();
	}

	return (
		<S.Container>
			<Grid container spacing={4}>
				<Grid item xs={12}>
					<Typography variant="h6" color="textPrimary">
						<strong>Asset Name*</strong>
					</Typography>

					<TextField
						placeholder="Item name"
						variant="outlined"
						size="medium"
						name="fullname"
						fullWidth
						type="text"
						value={formName}
						onBlur={() => dispatch(updateField({ name: 'name', value: formName }))}
						onChange={(e) => setFormName(e.target.value)}
					/>
				</Grid>

				<Grid item xs={12}>
					<Typography variant="h6" color="textPrimary">
						<strong>Collection Name</strong>
					</Typography>

					<Autocomplete 
						value={collectionName}
						onChange={(event: any, newValue: string | null) => {
							setCollectionName(newValue || '');
						}}		
						options={['', ...collections]}
						getOptionSelected={(option: string, value: string) => option === value}				
						renderInput={(params) => {
							return (
								<TextField
									{...params}

									placeholder="Collection name"
									variant="outlined"
									size="medium"
									name="collection-name"
									fullWidth
									type="text"
									value={collectionName}
									onBlur={() => dispatch(updateField({ name: 'collection_name', value: collectionName }))}
									onChange={(e) => setCollectionName(e.target.value)}
								/>							
							);
						}}
					/>
				</Grid>

				<Grid item xs={12}>
					<Typography variant="h6" color="textPrimary">
						<strong>Category</strong>
					</Typography>

					<FormControl fullWidth>
						<InputLabel style={{ marginLeft: 16 }} >Select the category</InputLabel>
						<Select
							value={category}
							label="Select the category"
							onChange={handleCategoryChange}
							onBlurCapture={() => dispatch(updateField({ name: 'category', value: category }))}
							variant='outlined'
							fullWidth 
						>
						{ categories.map((category, index) => (
							<MenuItem key={`category-${index}`} value={category}>{category}</MenuItem>
						))}
						</Select>
					</FormControl>
				</Grid>

				<Grid item xs={12}>
					<Typography variant="h6" color="textPrimary">
						<strong>Tags List (separate with space or comma)</strong>
					</Typography>

					<div>
						<S.TagContainer>
							{tags.map((tag, index) => (
								<S.Tag key={`tag-${index}`}>
									{tag} <MdClose onClick={() => handleDeleteTag(index)} />
								</S.Tag>
							))}
							<S.TagInput 
								placeholder={ tags.length >= 5 ? '' : 'Add Tag'}
								disabled={tags.length >= 5} 
								value={tag} 
								onChange={(e) => { tags.length < 5 && setTag(e.target.value) }} 
								onKeyUp={handleCreateTag} 
								onBlur={() => dispatch(updateTags(tags))}
							/>
						</S.TagContainer>
					</div>
				</Grid>

				<Grid item xs={12}>
					<Typography variant="h6" color="textPrimary">
						<strong>Description</strong>
					</Typography>

					<TextField
						placeholder="Your description"
						variant="outlined"
						name="description"
						fullWidth
						multiline
						rows={6}
						value={formDescription}
						onBlur={() => dispatch(updateField({ name: 'description', value: formDescription }))}
						onChange={(e) => setFormDescription(e.target.value)}
					/>
				</Grid>

				<Grid item xs={12}>
					<Typography variant="h6" color="textPrimary">
						<strong>Editions*</strong>
					</Typography>

					<TextField
						placeholder="Number of editions"
						variant="outlined"
						size="medium"
						name="editions"
						inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
						fullWidth
						type="numeric"
						error={!!errorMessage.editions}
						helperText={errorMessage.editions}
						value={editionsToken}
						onBlur={() => dispatch(updateEditions(Number(editionsToken)))}
						onChange={handleEditions}
					/>
				</Grid>

				<Grid item xs={12}>
					<Typography variant="h6" color="textPrimary">
						<strong>Royalties (%)</strong>
					</Typography>

					<TextField
						placeholder="Royalties"
						variant="outlined"
						size="medium"
						name="royalties"
						fullWidth
						type="text"
						error={!!errorMessage.royalties}
						helperText={errorMessage.royalties}
						value={royaltiesToken}
						onBlur={() => { 
							dispatch(updateRoyalties(Number(royaltiesToken))); 
							setErrorMessage({ ...errorMessage, royalties: '' })
						}}
						onChange={(e) => handleFormattedRoyalties(e)}
					/>

					<Hidden>
					<Box display="flex" alignItems="center" justifyContent="space-between">
						<Typography>
							Apply royalty sharing
							<Tooltip title="Royalty may be shared with co-creator(s)">
								<IconButton>
									<MdHelpOutline />
								</IconButton>
							</Tooltip>
						</Typography>

						<Switch color="primary" checked={checkedRoyalty} onChange={handleCheckedRoyaltySharing} />
					</Box>

					{ checkedRoyalty && (
					<Box display="flex" flexDirection="column">
						{ royaltiesList.map((item, index) => (
						<S.RoyaltyContainer key={index}>
							<Box display="flex" flexDirection="column">
								<Typography variant="subtitle1" color="textPrimary">
									<strong>Share with</strong>
								</Typography>

								<TextField
									placeholder="Wallet Address"
									variant="outlined"
									size="medium"
									name={`wallet-address-${index}`}
									type="text"
									error={!!royaltiesSharingError[index]}
									helperText={royaltiesSharingError[index]}
									onChange={(e) => handleRoyaltiesList(index, 'walletAddress', e.target.value)}
									value={item.walletAddress}
									disabled={index === 0}
								/>
							</Box>

							<Box display="flex" flexDirection="column">
								<Typography variant="subtitle1" color="textPrimary">
									<strong>Rate (%)</strong>
								</Typography>

								<TextField
									placeholder="Rate"
									variant="outlined"
									size="medium"
									name={`wallet-royalty-${index}`}
									type="text"
									value={royaltiesList[index].royalties}
									onChange={(e) => handleRoyaltiesList(index, 'royalties', e.target.value)}
									onBlur={handleRoyaltiesSharing}
									disabled={index === 0}
								/>
							</Box>

							{ !(item.walletAddress === system.tzPublicKey!) && (
							<Box display="flex" alignItems="center" justifyContent="center" ml={-4} pt={4} pb={4}>
								<IconButton onClick={() => handleDeleteRoyalties(index)}>
									<MdRemoveCircleOutline />
								</IconButton>
							</Box>
							)}
						</S.RoyaltyContainer>								
						))}

						<Box display="flex" alignItems="center" justifyContent="center" mt={2}>
							<IconButton onClick={handleAddRoyalties}>
								<MdAddCircleOutline />
							</IconButton>
						</Box>
					</Box>
					)}
					</Hidden>
				</Grid>

				<Grid item xs={12}>
					<Button variant="contained" type="button" color="primary" size="large" onClick={handleCreateToken}>
						Next
					</Button>
				</Grid>
			</Grid>
		</S.Container>
	);
}